import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatMoney, BACKEND_URL } from "@/lib/api";
import { FileText, Briefcase, House, Clock, CheckCircle, CreditCard, ArrowRight, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import DocumentBrowser from "@/components/DocumentBrowser";

export function PortalLayout() {
  const { user, loading } = useAuth();
  if (loading) return <div className="py-32 text-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const NAV = [
    { to: "/portal", icon: House, label: "Dashboard", end: true },
    { to: "/portal/projects", icon: Briefcase, label: "Projects" },
    { to: "/portal/invoices", icon: FileText, label: "Invoices" },
  ];

  return (
    <div data-testid="portal-layout" className="fade-in py-12">
      <div className="container-gos">
        <div className="overline mb-2">Client portal</div>
        <h1 className="font-display text-3xl text-gos-charcoal tracking-tight">Welcome, {user.full_name}</h1>
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10 mt-10">
          <aside className="space-y-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end}
                data-testid={`portal-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 text-sm rounded-sm ${isActive ? "bg-gos-navy text-white" : "text-gos-slate hover:bg-gos-subtle"}`
                }
              >
                <n.icon size={16} /> {n.label}
              </NavLink>
            ))}
          </aside>
          <div><Outlet /></div>
        </div>
      </div>
    </div>
  );
}

export function PortalDashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  useEffect(() => {
    api.get("/portal/projects").then((r) => setProjects(r.data));
    api.get("/portal/invoices").then((r) => setInvoices(r.data));
  }, []);

  const openInvoices = invoices.filter((i) => i.status !== "paid").length;

  return (
    <div data-testid="portal-dashboard">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <Stat label="Active projects" value={projects.length} icon={Briefcase} />
        <Stat label="Open invoices" value={openInvoices} icon={FileText} />
        <Stat label="Account" value={user?.company || user?.email} icon={House} />
      </div>

      <div className="mb-8">
        <div className="overline mb-3">Recent projects</div>
        <div className="space-y-3">
          {projects.slice(0, 3).map((p) => (
            <Link key={p.id} to={`/portal/projects/${p.id}`} data-testid={`portal-project-${p.id}`} className="block bg-white border border-gos-border p-4 rounded-sm card-lift">
              <div className="flex justify-between items-center mb-2">
                <div className="font-display text-lg text-gos-navy">{p.title}</div>
                <span className="text-xs bg-gos-subtle px-2 py-0.5 rounded-sm uppercase tracking-widest text-gos-charcoal">{p.status}</span>
              </div>
              <div className="text-xs text-gos-muted">{p.target_country} · {p.services.join(", ")}</div>
              <div className="mt-3 h-1.5 bg-gos-subtle rounded-full overflow-hidden">
                <div className="h-full bg-gos-sand" style={{ width: `${p.progress_percent || 0}%` }} />
              </div>
              <div className="mt-2 text-xs text-gos-muted">{p.progress_percent || 0}% complete</div>
            </Link>
          ))}
          {projects.length === 0 && <div className="text-sm text-gos-muted">No projects yet. Once you sign up for a service, your project will appear here.</div>}
        </div>
      </div>

      <div>
        <div className="overline mb-3">Latest invoices</div>
        <div className="space-y-2">
          {invoices.slice(0, 3).map((inv) => (
            <Link key={inv.id} to={`/portal/invoices/${inv.id}`} className="bg-white border border-gos-border p-4 rounded-sm flex justify-between items-center card-lift">
              <div>
                <div className="font-mono text-sm text-gos-charcoal">{inv.number}</div>
                <div className="text-xs text-gos-muted">{inv.due_date ? `Due ${inv.due_date}` : ""}</div>
              </div>
              <div className="text-right">
                <div className="font-display text-lg text-gos-navy">{formatMoney(inv.total, inv.currency)}</div>
                <span className={`text-[10px] uppercase tracking-widest ${inv.status === "paid" ? "text-gos-success" : "text-gos-sand"}`}>{inv.status}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export function PortalProjects() {
  const [projects, setProjects] = useState([]);
  useEffect(() => { api.get("/portal/projects").then((r) => setProjects(r.data)); }, []);
  return (
    <div data-testid="portal-projects-list">
      <div className="overline mb-3">All projects</div>
      <div className="space-y-3">
        {projects.map((p) => (
          <Link key={p.id} to={`/portal/projects/${p.id}`} className="block bg-white border border-gos-border p-5 rounded-sm card-lift">
            <div className="flex justify-between mb-2">
              <div className="font-display text-lg text-gos-navy">{p.title}</div>
              <span className="text-xs bg-gos-subtle px-2 py-0.5 uppercase tracking-widest">{p.status}</span>
            </div>
            <div className="text-xs text-gos-muted">{p.target_country}</div>
            <div className="mt-3 h-1.5 bg-gos-subtle rounded-full overflow-hidden">
              <div className="h-full bg-gos-sand" style={{ width: `${p.progress_percent || 0}%` }} />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function PortalProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  useEffect(() => { api.get(`/portal/projects/${id}`).then((r) => setProject(r.data)); }, [id]);
  if (!project) return <div>Loading…</div>;
  return (
    <div data-testid={`portal-project-detail-${id}`}>
      <Link to="/portal/projects" className="text-sm text-gos-navy hover:text-gos-sand">← Back</Link>
      <h2 className="font-display text-3xl text-gos-charcoal mt-2">{project.title}</h2>
      <div className="text-sm text-gos-muted mt-1">{project.target_country} · {project.services.join(", ")}</div>
      <div className="mt-6 h-2 bg-gos-subtle rounded-full overflow-hidden">
        <div className="h-full bg-gos-sand" style={{ width: `${project.progress_percent}%` }} />
      </div>

      <div className="mt-10">
        <div className="overline mb-3">Tasks</div>
        <div className="space-y-2">
          {project.tasks.map((t) => (
            <div key={t.id} className="flex items-center gap-3 bg-white border border-gos-border p-3 rounded-sm">
              {t.status === "done" ? <CheckCircle size={18} weight="duotone" className="text-gos-success" /> : <Clock size={18} className="text-gos-sand" />}
              <span className="text-sm text-gos-charcoal flex-1">{t.title}</span>
              <span className="text-xs uppercase tracking-widest text-gos-muted">{t.status}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10">
        <div className="overline mb-3">Documents</div>
        <DocumentBrowser projectId={project.id} />
      </div>
    </div>
  );
}

export function PortalInvoices() {
  const [invoices, setInvoices] = useState([]);
  const navigate = useNavigate();
  useEffect(() => { api.get("/portal/invoices").then((r) => setInvoices(r.data)); }, []);

  const payDeposit = async (inv) => {
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/payments/checkout", { invoice_id: inv.id, origin_url: origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Payment session failed");
    }
  };

  return (
    <div data-testid="portal-invoices-list">
      <div className="overline mb-3">Invoices</div>
      <div className="space-y-3">
        {invoices.map((inv) => (
          <div key={inv.id} data-testid={`invoice-row-${inv.number}`} className="bg-white border border-gos-border p-5 rounded-sm">
            <div className="flex justify-between items-start gap-4">
              <div>
                <div className="font-mono text-sm text-gos-charcoal">{inv.number}</div>
                <div className="text-xs text-gos-muted mt-1">{inv.due_date ? `Due ${inv.due_date}` : ""}</div>
                <div className="mt-3 space-y-1 text-xs">
                  {inv.items.map((it, i) => (
                    <div key={`${inv.number}-${i}-${it.description?.slice(0, 24)}`} className="flex justify-between"><span className="text-gos-slate">{it.description} × {it.qty}</span><span className="font-mono">{formatMoney(it.line_total, inv.currency)}</span></div>
                  ))}
                </div>
              </div>
              <div className="text-right">
                <div className="font-display text-xl text-gos-navy">{formatMoney(inv.total, inv.currency)}</div>
                <span className={`text-[10px] uppercase tracking-widest ${inv.status === "paid" ? "text-gos-success" : "text-gos-sand"}`}>{inv.status}</span>
                {inv.status !== "paid" && (
                  <button onClick={() => payDeposit(inv)} data-testid={`pay-deposit-btn-${inv.id}`} className="btn-primary mt-3 text-xs !px-3 !py-2">
                    <CreditCard size={14} weight="bold" /> Pay 50% deposit
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-gos-border p-5 rounded-sm">
      <Icon size={20} className="text-gos-sand" />
      <div className="overline mt-3 !text-gos-muted">{label}</div>
      <div className="font-display text-2xl text-gos-navy mt-1 truncate">{value}</div>
    </div>
  );
}

export function PaymentSuccess() {
  const [search] = [new URLSearchParams(window.location.search)];
  const sessionId = search.get("session_id");
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setStatus("paid");
        if (data.status === "expired") return setStatus("expired");
        if (attempts++ < 8) setTimeout(poll, 2000);
        else setStatus("pending");
      } catch (e) { setStatus("error"); }
    };
    poll();
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="fade-in py-32 text-center">
      <div className="container-gos max-w-md">
        {status === "paid" && (
          <>
            <CheckCircle size={48} weight="duotone" className="text-gos-success mx-auto" />
            <h1 className="font-display text-3xl mt-4">Payment received.</h1>
            <p className="text-gos-slate mt-2">Thank you — your project will move to the next phase.</p>
          </>
        )}
        {status === "checking" && <h1 className="font-display text-3xl">Confirming payment…</h1>}
        {status === "expired" && <h1 className="font-display text-3xl">Session expired.</h1>}
        {status === "pending" && <h1 className="font-display text-3xl">Still processing — check email shortly.</h1>}
        {status === "error" && <h1 className="font-display text-3xl">Something went wrong.</h1>}
        <Link to="/portal/invoices" className="btn-secondary mt-8 inline-flex">Back to invoices <ArrowRight size={14} /></Link>
      </div>
    </div>
  );
}
